// A puzzle in progress, and a puzzle finished, kept on this device.
//
// A daily is played once. Leave partway through and the board comes back as it
// was; come back after finishing and the result screen comes back, not a fresh
// board. Both are the same mechanism: the guesses made so far are stored, and
// everything else on screen is worked out from them.
//
// **Only the arrangement and the arrangements submitted are stored.** Not the
// marks: the device does not know the right order -- the server does, and works
// the marks out again from the arrangements whenever the game is opened
// (ro_check). Which cards are locked, and whether the game is over, follow from
// the marks, so none of that is stored either.
//
// Nothing here is a TMDB copy: film IDs, booleans and a timestamp, the same
// shape a stored result takes (CLAUDE.md: Results store title IDs, not names).
import { MAX_GUESSES, type GuessMarks } from './types.ts';

const KEY = 'cinemore-running-order';
// Enough to cover any archive a player is likely to work through in a sitting,
// and far short of what would make this worth thinking about again.
const KEEP = 60;

export interface Progress {
  order: number[];        // film IDs, in slot order
  submitted: number[][];  // every arrangement submitted as a guess, in order
  at: number;             // last touched, for pruning
}

type Store = Record<string, Progress>;

// Storage throws rather than returning null in a private window with site data
// blocked, and a half-written or hand-edited value parses to anything at all, so
// every read is guarded and every failure means "no saved game" — never a crash
// on the way into a puzzle.
function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Store : {};
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Blocked or full. The game plays perfectly well without being saved; it
    // just won't come back, and that is not worth an error in front of a player.
  }
}

// A saved game is only usable if it still fits the puzzle it claims to be. A
// puzzle can be regenerated before it publishes, and a player can edit their own
// storage, so the arrangement has to be exactly the same films rearranged.
export function usable(entry: unknown, films: number[]): Progress | null {
  if (!entry || typeof entry !== 'object') return null;
  const { order, submitted, at } = entry as Partial<Progress>;
  // The same films, each once: a permutation, not just the right length.
  const wanted = [...films].sort((a, b) => a - b).join();
  const fits = (row: unknown): row is number[] => Array.isArray(row) && row.length === films.length
    && row.every((id) => typeof id === 'number') && [...row].sort((a, b) => a - b).join() === wanted;
  if (!fits(order)) return null;
  // An entry saved before guesses were checked by the server has marks and no
  // arrangements, and cannot be replayed: it is dropped, not repaired.
  if (!Array.isArray(submitted) || submitted.length > MAX_GUESSES || !submitted.every(fits)) return null;
  return { order, submitted, at: typeof at === 'number' ? at : 0 };
}

// Oldest first out, so a long archive session can't grow this without limit.
export function pruned(store: Store, keep = KEEP): Store {
  const entries = Object.entries(store);
  if (entries.length <= keep) return store;
  entries.sort(([, a], [, b]) => b.at - a.at);
  return Object.fromEntries(entries.slice(0, keep));
}

export function loadProgress(number: number, films: number[]): Progress | null {
  return usable(read()[String(number)], films);
}

export function saveProgress(number: number, order: number[], submitted: number[][]): void {
  const store = read();
  store[String(number)] = { order, submitted, at: Date.now() };
  write(pruned(store));
}

// Which cards are locked, worked out from the guesses rather than stored beside
// them: a slot is locked once any guess has placed it correctly, and locking is
// only ever added to.
export function lockedFrom(guesses: GuessMarks[], slots: number): boolean[] {
  return Array.from({ length: slots }, (_, i) => guesses.some((row) => row[i] === true));
}
